import { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import Button from "../../component-lib/Molecules/button/Button";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import { plusIcon, searchIcon } from "../../assets";
import MenuDropdown from "../../component-lib/Molecules/menuDropdown/MenuDropdown";
import useDeviceType from "../../custom-hooks/DeviceType";
import { useNavigate } from "react-router-dom";

import CreatePatientDrawer from "./create-patient-drawer/CreatePatientDrawer";
import { TEXT_CONSTANTS } from "../../utils/text-constants/textConstants";
import SearchSelectDropdown from "../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import {
  componentKey,
  getSearchedPatientList,
  setCloseDispositionDialog,
  setCloseEMSSceneVisit,
  setRefreshPatientList,
} from "./patientSlice";
import { useDispatch, useSelector } from "react-redux";
import { getPatientList, patchPatient } from "./patientSaga";
import {
  destructStatusData,
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
} from "../../utils/statusConstants";
import TooltipForTable from "../../component-lib/Molecules/tooltip/TooltipForTable";
import SelectDropdown from "../../component-lib/Atoms/Select/Select";
import CreateEMSSceneDialog from "./patient-details/dialog/CreateEMSSceneDialog";
import EMSTransferDialog from "./patient-details/dialog/EMSTransferDialog";
import { getUserPermission } from "../../utils/getToken";
import { getRoomDisplayName } from "../../utils/room/roomDisplay";

const PatientScreen = () => {
  const {
    closeCreatePatientDrawer,
    patientListData,
    refreshPatientList,
    closeEMSSceneVisit,
    patientIdFromParticipant,
    closeDispositionDialog,
    tempVisitData,
  } = useSelector((state: any) => state[componentKey]);

  const userPermissionData: any = getUserPermission();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // USING THIS HOOK SET INITIAL ROW PER
  const [rowPerPage, setRowPerPage] = useState("10");
  // USING THIS HOOK SET INITIAL PAGE NUMBER
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [allocated, setAllocated] = useState("");
  const [openEMSSceneDialog, setOpenEMSSceneDialog] = useState(false);
  const [openEMSTransferDialog, setOpenEMSTransferDialog] = useState(false);

  // ADDED HALF MILLISECOND DELAY IN SEARCH USING THIS HOOK
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const deviceType = useDeviceType();

  // CLOSE CREATE PATIENT DRAWER WHEN NEW PATIENT ADDED
  useEffect(() => {
    if (closeCreatePatientDrawer) {
      setIsDrawerOpen(false);
      dispatch(getSearchedPatientList(null));
    }
  }, [closeCreatePatientDrawer]);

  useEffect(() => {
    if (closeEMSSceneVisit) {
      dispatch(setCloseEMSSceneVisit(false));
      setOpenEMSSceneDialog(false);
      patientList();
      setOpenEMSTransferDialog(true);
    }
  }, [closeEMSSceneVisit]);

  useEffect(() => {
    if (closeDispositionDialog) {
      navigate(`/patient/${tempVisitData?.patientId}?tab=visits`);
      dispatch(setCloseDispositionDialog(false));
      setOpenEMSTransferDialog(false);
    }
  }, [closeDispositionDialog]);

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  // PATIENT LIST COLUMN DATA
  const patientColumns = [
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.bsaId}
          </p>
        );
      },
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: any) => {
        return (
          <Tooltip
            title={
              row?.assignedProviderName
                ? `Assigned Provider: ${row?.assignedProviderName}`
                : "No provider assigned"
            }
            arrow
            placement="top"
          >
            <p
              className={`font-medium text-primary-main ${
                userPermissionData?.patient?.view
                  ? "cursor-pointer"
                  : "cursor-default"
              }`}
              onClick={() => {
                if (userPermissionData?.patient?.view) {
                  localStorage.removeItem("selectedTab");
                  navigate(`/patient/${row?.patientId}`);
                }
              }}
            >
              {row?.patientName}
            </p>
          </Tooltip>
        );
      },
    },
    {
      field: "facility",
      label: <div>Facility</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.facility}
          </p>
        );
      },
    },
    {
      field: "gender",
      label: <div>Gender</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.gender}</p>;
      },
    },
    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.visitType}</p>;
      },
    },
    {
      field: "clinicLocation",
      label: <div>Unit Number</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.baseCamp}</p>;
      },
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            <TooltipForTable label={row?.chiefComplaint} />
          </p>
        );
      },
    },
    {
      field: "department",
      label: <div>Department</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.department}</p>;
      },
    },
    {
      field: "room",
      label: <div>Room</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {getRoomDisplayName(row?.room) || "-"}
          </p>
        );
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getStatusTextColor(row?.status),
                background: getStatusBackground(row?.status),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getStatusDotColor(row?.status),
                  borderRadius: "50%",
                }}
              ></span>
              {destructStatusData(row?.status)}
            </span>
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        const actionList = [
          {
            label: "View Patient",
            onClickCb: () => {
              if (userPermissionData?.patient?.view) {
                // Navigate only if user has permission
                localStorage.removeItem("selectedTab");
                navigate(`/patient/${row?.patientId}`);
              }
            },
            disabled: !userPermissionData?.patient?.view, // Disable if permission is false or undefined
          },
        ];

        // if (
        //   !(
        //     row?.status === "transferOffProperty" ||
        //     row?.status === "discharged"
        //   ) &&
        //   row?.triage?.length
        // )

        // added this for to show by default assign to me action for self care
        // (and Listening Ear, which has no triage requirement)
        if (
          row?.department === "Self Care" ||
          row?.department?.toLowerCase()?.includes("ear") ||
          !["transferOffProperty", "discharged"].includes(row?.status)
        ) {
          actionList.push({
            label: row?.isAssigned ? "Unassign" : "Assign to me",
            onClickCb: () => {
              const payload = {
                id: row?.patientId,
                apiPayload: {
                  assigenProvider: JSON.stringify(!row?.isAssigned),
                },
              };
              dispatch(patchPatient(payload));
            },
            disabled: false, //set disabled to false for view patient
          });
        }
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown options={actionList} />
            </div>
          </>
        );
      },
    },
  ];

  // PATIENT TAB ROW DATA
  const patientData = patientListData?.patient?.map((data: any) => {
    return {
      patientId: data?.id,
      bsaId: data?.participant?.bsaId || "-",
      patientName:
        `${data?.participant?.firstName} ${data?.participant?.lastName}` || "-",
      gender: data?.participant?.gender || "-",
      facility: data?.facility?.name || "-",
      visitType:
        data?.visitType === "EMS_Visit"
          ? "EMS"
          : data?.visitType === "WALK_In_Visit"
            ? "Walk-In"
            : data?.visitType === "Scene"
              ? "EMS Scene"
              : "-",
      baseCamp: data?.participant?.baseCamp?.unitNumber || "-",
      chiefComplaint: data?.visit?.[0]?.chiefComplaints || "-",
      department: data?.visit[0]?.typeOfCare
        ? data.visit[0].typeOfCare.split("_").join(" ")
        : "-",
      status: data?.status || "-",
      isAssigned: data?.isAssigned || false,
      triage: data?.visit?.[0]?.triage,
      room: data?.room?.room,
      assignedProviderName: data?.assignedProviderName || null,
    };
  });

  // GET PATIENT LIST DATA
  useEffect(() => {
    if (searchedValue.trim()?.length) {
      const payload = {
        pageNumber: pageNumber,
        searchKey: searchedValue,
        limit: rowPerPage,
        allocated: allocated ? allocated : "",
      };
      dispatch(getPatientList(payload));
    } else {
      patientList();
    }
  }, [rowPerPage, pageNumber, searchedValue, allocated]);

  useEffect(() => {
    if (refreshPatientList) {
      dispatch(setRefreshPatientList(false));
      const payload = {
        pageNumber: pageNumber,
        searchKey: searchedValue,
        limit: rowPerPage,
        allocated: allocated ? allocated : "",
      };
      dispatch(getPatientList(payload));
    }
  }, [refreshPatientList]);

  const patientList = () => {
    const payload = {
      pageNumber: pageNumber,
      searchKey: searchedValue,
      limit: rowPerPage,
      allocated: allocated ? allocated : "",
    };
    dispatch(getPatientList(payload));
  };

  return (
    // Fills the space left by App's flex column (no viewport math).
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full min-w-0 gap-3">
        {/* Wraps below xl so the toolbar drops to its own line on tablets
            instead of widening the page. */}
        <div
          className={`flex flex-wrap gap-3 ${deviceType === "mobile" ? "flex-col items-start" : "items-center"} justify-between w-full`}
        >
          <div className="w-auto flex items-center">
            {/* No negative margin: md:-mt-14 pulled this 56px upward from
                768px and lg:mt-0 only cancelled it from 1024px, so on iPad
                portrait it overlapped the app header. */}
            <span className="text-[18px] font-semibold font-inter text-[#1D1D1D] whitespace-nowrap">
              Patient List
            </span>
          </div>

          <div
            className={`flex flex-wrap items-start gap-3 ${deviceType === "mobile" ? "w-full justify-between" : ""}`}
            style={{ justifyContent: "flex-end" }}
          >
            <div>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e) => handleChange(e)}
                size="small"
                label="Search By Patient Name, BSA ID"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            <div className="flex items-center gap-3">
              <div style={{ maxWidth: "250px", minWidth: "180px" }}>
                <SelectDropdown
                  label=""
                  name="allocated"
                  displayEmpty={true}
                  placeholderDisable={false}
                  value={allocated}
                  options={[
                    { label: "All Assignment", value: "" },
                    { label: "Assigned", value: "assigned" },
                    { label: "Un Assigned", value: "unassigned" },
                  ]}
                  onChangeCb={(e) => {
                    setPageNumber(1);
                    setAllocated(e.target.value);
                  }}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  disabled={!userPermissionData?.patient?.create}
                  label="Create Clinic Visit"
                  variant="contained"
                  onClickCb={() => {
                    setIsDrawerOpen(true);
                    localStorage.removeItem("selectedTab");
                  }}
                  style={{
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    minWidth: "155px",
                  }}
                />

                <Button
                  disabled={!userPermissionData?.visit?.create}
                  label="Create EMS Visit"
                  variant="contained"
                  onClickCb={() => {
                    navigate("/patient/jot-form");
                    // setOpenEMSSceneDialog(true);
                  }}
                  style={{
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    minWidth: "155px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {/* min-w-0 lets this flex child shrink, so the wide table scrolls
            inside this container instead of widening the whole page. */}
        <div className="w-full min-w-0" style={{ overflow: "auto" }}>
          <Table
            columns={patientColumns}
            rows={patientData}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: patientListData?.totalPages,
              totalCount: patientListData?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      {/* CREATE PATIENT DIALOG */}
      {isDrawerOpen && (
        <CreatePatientDrawer
          open={isDrawerOpen}
          closeCb={() => {
            setIsDrawerOpen(false);
          }}
        />
      )}
      {openEMSSceneDialog && (
        <CreateEMSSceneDialog
          dialogHeading="Create EMS Visit"
          handleClose={() => {
            setOpenEMSSceneDialog(false);
          }}
          isOpen={openEMSSceneDialog}
        />
      )}
      {openEMSTransferDialog && (
        <EMSTransferDialog
          open={openEMSTransferDialog}
          patientId={patientIdFromParticipant}
          handleCloseDialog={() => {
            setOpenEMSTransferDialog(false);
          }}
        />
      )}
    </WrapperCard>
  );
};

export default PatientScreen;
